import { ChangeDetectorRef, Component, ElementRef } from '@angular/core';

import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import { DomoService, DomoValidationStatus } from '../services/domo.service';
import {
  ClientCredentials,
  OAuthService,
} from 'src/app/components/content-oauth/services/oauth.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'template-component-domo',
  templateUrl: './domo.component.html',
  styleUrls: ['./domo.component.scss'],
  standalone: false,
})
export class DomoComponent {
  isLoading = false;
  isValidating = true;
  componentId: string;
  source: string;
  filters: string;
  credentials: ClientCredentials | null = null;
  validationStatus: DomoValidationStatus = { status: 'UNCHECKED' };
  companyId: string | null = null;

  constructor(
    private elementRef: ElementRef,
    private componentsFactory: ComponentsService,
    private attributeDataFactory: AttributeDataService,
    private changeDetectorRef: ChangeDetectorRef,
    private companyStateService: CompanyStateService,
    private domoService: DomoService,
    private oAuthService: OAuthService,
  ) {
    componentsFactory.registerDirective({
      type: 'rise-domo',
      element: this.elementRef.nativeElement,
      show: () => {
        this.componentId = this.componentsFactory.selected.id;
        this._load();
      },
    });
  }

  async _load() {
    this.isLoading = true;

    this.source =
      this.attributeDataFactory.getAvailableAttributeData(
        this.componentId,
        'src',
      ) || '';

    this.filters =
      this.attributeDataFactory.getAvailableAttributeData(
        this.componentId,
        'filters',
      ) || '';

    await this._getCredentials();
    await this._validateAttributes();
  }

  async _getCredentials() {
    this.credentials = null;

    const company = this.companyStateService.getCopyOfSelectedCompany(true);
    this.companyId = company.id;
    if (
      company?.settings?.domoEnabled === true ||
      company?.settings?.domoEnabled === 'true'
    ) {
      const credentials = await this.oAuthService.getCompanyCredentials(
        this.companyId,
        'domo',
      );
      if (credentials?.clientId && credentials?.clientSecret) {
        this.credentials = credentials;
      }
    }
  }

  async _validateAttributes() {
    if (!this.source || this.source.trim() === '') {
      this.validationStatus = { status: 'UNCHECKED' };
      this.isLoading = false;
      this.isValidating = false;
      this.changeDetectorRef.detectChanges();
      return;
    }

    this.isValidating = true;

    try {
      const signature = await this.domoService.getSignature(this.companyId);
      const status = await this.domoService.validateSourceAndCredentials(
        this.source,
        this.credentials,
        this.companyId,
        signature,
        this.filters,
      );
      this.validationStatus = status;
      if (status.status === 'VALID') {
        this.saveSignature(signature);
      }
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      console.error('Validation error:', error);
      this.validationStatus = { status: 'INTERNAL_ERROR' };
      this.changeDetectorRef.detectChanges();
    } finally {
      this.isLoading = false;
      this.isValidating = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  saveAttributeData() {
    const attributes: { [key: string]: string } = {
      environment: environment.production ? 'prod' : 'test',
      src: this.source,
      filters: this.filters,
    };
    this.attributeDataFactory.setMultipleAttributeData(
      this.componentId,
      attributes,
    );
  }

  async saveSignature(signature: string) {
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'signature',
      signature,
    );
  }

  onAttributeChanged() {
    this.saveAttributeData();
    this._validateAttributes();
    this.changeDetectorRef.detectChanges();
  }
}
